import m from 'mithril'
import type { ListSummary, ListVisibility } from '../../shared/types.ts'
import { api } from '../api.ts'
import { state } from '../state.ts'
import { Empty, ErrorNote, Spinner, VisibilityPicker, visibilityLabel } from '../ui.ts'
import { Sheet } from './Sheet.ts'

// The starter lists sit on top with their own icons, matching the buttons on a restaurant.
const PINNED: Record<string, string> = {
  favorites: 'M12 20.3s-7.8-4.7-7.8-10.4A4.4 4.4 0 0 1 12 7.2a4.4 4.4 0 0 1 7.8 2.7c0 5.7-7.8 10.4-7.8 10.4z',
  'want-to-go': 'M5.5 21V4.5M5.5 4.5h12l-2.6 4.2 2.6 4.3h-12',
}
const LIST_ICON = 'M4 6.5h16M4 12h16M4 17.5h10'
const CHEVRON = 'M9 5l7 7-7 7'

const icon = (d: string, size = 20) =>
  m(
    'svg',
    {
      viewBox: '0 0 24 24',
      width: size,
      height: size,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.9,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
    },
    m('path', { d }),
  )

/** The Lists tab: every list you own, starter lists first, and a way to make a new one. */
export const ListsView: m.ClosureComponent = () => {
  let lists: ListSummary[] | null = null
  let error = ''
  let creating = false
  let name = ''
  let visibility: ListVisibility = 'public'
  let busy = false

  const load = async () => {
    if (!state.me) return
    try {
      lists = await api<ListSummary[]>('/api/me/lists')
    } catch (err) {
      error = (err as Error).message
    }
    m.redraw()
  }

  const create = async (event: Event) => {
    event.preventDefault()
    if (!name.trim()) return
    busy = true
    try {
      const list = await api<ListSummary>('/api/me/lists', { method: 'POST', body: { name, visibility } })
      m.route.set(`/l/${list.id}`)
    } catch (err) {
      error = (err as Error).message
    } finally {
      busy = false
      m.redraw()
    }
  }

  const row = (list: ListSummary) =>
    m(m.route.Link, { href: `/l/${list.id}`, key: list.id, class: 'list-row' }, [
      m('span.list-row-icon', { class: PINNED[list.slug] ? 'pinned' : '' }, icon(PINNED[list.slug] ?? LIST_ICON)),
      m('div.list-row-main', [
        m('div.list-row-title', list.name),
        m(
          'div.list-row-meta',
          `${list.itemCount} place${list.itemCount === 1 ? '' : 's'}${list.visibility === 'public' ? '' : ` · ${visibilityLabel(list.visibility)}`}`,
        ),
      ]),
      m('span.list-row-chevron', icon(CHEVRON, 18)),
    ])

  return {
    oninit: load,
    view: () => {
      if (!state.me) {
        return m('section.stack', [
          m(Empty, { text: 'Sign in to make lists' }),
          m(m.route.Link, { href: '/login', class: 'button primary' }, 'Sign in'),
        ])
      }
      if (error && !lists) return m(ErrorNote, { text: error })
      if (!lists) return m(Spinner)

      const pinned = Object.keys(PINNED)
        .map((slug) => lists!.find((list) => list.slug === slug))
        .filter((list) => list != null)
      const others = lists.filter((list) => !(list.slug in PINNED))

      return m('section.stack', [
        pinned.length ? m('div.list-group', pinned.map(row)) : null,
        m('div.section-row', [
          m('p.section-label', 'Your lists'),
          m('button.link-button', { type: 'button', onclick: () => (creating = true) }, '+ New list'),
        ]),
        others.length
          ? m('div.list-group', others.map(row))
          : m(Empty, { text: 'No lists yet' }),

        creating
          ? m(Sheet, { title: 'New list', onclose: () => (creating = false) }, [
              m('form.stack', { onsubmit: create }, [
                m('label', [
                  'Name',
                  m('input', {
                    value: name,
                    placeholder: 'Best tacos',
                    oninput: (event: Event) => {
                      name = (event.target as HTMLInputElement).value
                    },
                  }),
                ]),
                m(VisibilityPicker, {
                  value: visibility,
                  onchange: (value: ListVisibility) => {
                    visibility = value
                  },
                }),
                error ? m(ErrorNote, { text: error }) : null,
                m('button.button.primary.block', { type: 'submit', disabled: busy || !name.trim() }, 'Create list'),
              ]),
            ])
          : null,
      ])
    },
  }
}
